/**
 * Admin Team Management Component
 * Manage admin users and send invitations
 */

import React, { useState, useEffect } from 'react';
import { Shield, UserPlus, Mail, Trash2, RefreshCw, Copy, CheckCircle } from 'lucide-react';
import {
    listAdmins,
    listInvitations,
    inviteAdmin,
    deleteAdmin,
    cancelInvitation,
    formatRole,
    getRoleBadgeColor,
} from '../services/adminManagementService';

interface AdminTeamManagementProps {
    onBack?: () => void;
}

const AdminTeamManagement: React.FC<AdminTeamManagementProps> = ({ onBack }) => {
    const [admins, setAdmins] = useState<any[]>([]);
    const [invitations, setInvitations] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

    // Invite form state
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('support_agent');
    const [inviteLoading, setInviteLoading] = useState(false);
    const [inviteError, setInviteError] = useState<string | null>(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        try {
            const [adminsData, invitationsData] = await Promise.all([listAdmins(), listInvitations()]);
            setAdmins(adminsData);
            setInvitations(invitationsData);
        } catch (err) {
            console.error('Error loading data:', err);
            setError(err instanceof Error ? err.message : 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleInviteSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setInviteError(null);
        setInviteLoading(true);

        try {
            await inviteAdmin(inviteEmail, inviteRole);
            setShowInviteModal(false);
            setInviteEmail('');
            setInviteRole('support_agent');
            await loadData();
        } catch (err) {
            console.error('Error sending invitation:', err);
            setInviteError(err instanceof Error ? err.message : 'Failed to send invitation');
        } finally {
            setInviteLoading(false);
        }
    };

    const handleDeleteAdmin = async (uid: string, email: string) => {
        if (!confirm(`Are you sure you want to revoke admin access for ${email}?`)) {
            return;
        }

        try {
            await deleteAdmin(uid);
            await loadData();
        } catch (err) {
            console.error('Error deleting admin:', err);
            setError(err instanceof Error ? err.message : 'Failed to delete admin');
        }
    };

    const handleCancelInvitation = async (token: string, email: string) => {
        if (!confirm(`Are you sure you want to cancel the invitation for ${email}?`)) {
            return;
        }

        try {
            await cancelInvitation(token);
            await loadData();
        } catch (err) {
            console.error('Error cancelling invitation:', err);
            setError(err instanceof Error ? err.message : 'Failed to cancel invitation');
        }
    };

    const handleCopyUrl = (url: string, token: string) => {
        navigator.clipboard.writeText(url);
        setCopiedUrl(token);
        setTimeout(() => setCopiedUrl(null), 2000);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 text-lg">Loading admin team...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white shadow-sm border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            {onBack && (
                                <button
                                    onClick={onBack}
                                    className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                                >
                                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                    <span className="font-medium">Back</span>
                                </button>
                            )}
                            <div className="flex items-center space-x-3">
                                <Shield className="w-6 h-6 text-indigo-600" />
                                <h1 className="text-2xl font-bold text-gray-900">Admin Team</h1>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={loadData}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                <RefreshCw className="w-4 h-4 inline mr-2" />
                                Refresh
                            </button>
                            <button
                                onClick={() => setShowInviteModal(true)}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                            >
                                <UserPlus className="w-4 h-4 inline mr-2" />
                                Invite Admin
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Error Alert */}
                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                        {error}
                    </div>
                )}

                {/* Admin Users */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">Active Admins ({admins.length})</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Admin
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Role
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Created
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Last Login
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {admins.map((admin) => (
                                    <tr key={admin.uid} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <div>
                                                <div className="font-medium text-gray-900">
                                                    {admin.displayName || admin.email.split('@')[0]}
                                                </div>
                                                <div className="text-sm text-gray-500">{admin.email}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(admin.role)}`}>
                                                {formatRole(admin.role)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {new Date(admin.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {admin.lastLogin ? new Date(admin.lastLogin).toLocaleDateString() : 'Never'}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                onClick={() => handleDeleteAdmin(admin.uid, admin.email)}
                                                className="text-red-600 hover:text-red-800 transition-colors"
                                                title="Revoke Access"
                                            >
                                                <Trash2 className="w-4 h-4 inline" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pending Invitations */}
                {invitations.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900">Pending Invitations ({invitations.length})</h2>
                        </div>
                        <div className="divide-y divide-gray-200">
                            {invitations.map((invite) => (
                                <div key={invite.token} className="px-6 py-4 flex items-center justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <Mail className="w-5 h-5 text-gray-400" />
                                            <span className="font-medium text-gray-900">{invite.email}</span>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(invite.role)}`}>
                                                {formatRole(invite.role)}
                                            </span>
                                            {invite.expired && (
                                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                    Expired
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            Invited by {invite.createdBy} • Expires {new Date(invite.expiresAt).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => handleCopyUrl(invite.registrationUrl, invite.token)}
                                            className="px-3 py-1 text-sm text-indigo-600 hover:text-indigo-800 transition-colors flex items-center"
                                        >
                                            {copiedUrl === invite.token ? (
                                                <>
                                                    <CheckCircle className="w-4 h-4 mr-1" />
                                                    Copied!
                                                </>
                                            ) : (
                                                <>
                                                    <Copy className="w-4 h-4 mr-1" />
                                                    Copy Link
                                                </>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => handleCancelInvitation(invite.token, invite.email)}
                                            className="text-red-600 hover:text-red-800 transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Invite Modal */}
            {showInviteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">Invite Admin User</h3>
                        </div>
                        <form onSubmit={handleInviteSubmit} className="px-6 py-4 space-y-4">
                            {inviteError && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                                    {inviteError}
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                                <input
                                    type="email"
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    required
                                    placeholder="admin@example.com"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                                <select
                                    value={inviteRole}
                                    onChange={(e) => setInviteRole(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                >
                                    <option value="support_agent">Support Agent</option>
                                    <option value="ops_manager">Operations Manager</option>
                                    <option value="super_admin">Super Admin</option>
                                </select>
                                <p className="mt-2 text-sm text-gray-500">
                                    Support Agent: Basic viewing • Ops Manager: Can modify • Super Admin: Full access
                                </p>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowInviteModal(false);
                                        setInviteError(null);
                                    }}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={inviteLoading}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition-colors"
                                >
                                    {inviteLoading ? 'Sending...' : 'Send Invitation'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminTeamManagement;
